import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import useOpenState from '../../../../hooks/useOpenState';
import MessageBox from '../../../../util/messageBox';
import {Divider, FormLabel, Grid, IconButton} from '@mui/material';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import _ from 'lodash';
import moment from 'moment';
import useDict from '../../../../hooks/useDict';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import Notes from '../../../../util/notes';
import useCliniciansAndNursesDict from '../../../../hooks/useCliniciansAndNursesDict';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import BedRequestWardSelect from './bedRequestWardSelect';
import {setSnackbar} from '../../../../store/reducers/globals';
import API from '../../../../app/api';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import clsx from 'clsx';
import {additionalData} from '../../../../store/reducers/additionalData';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {multiHospitalMode} from '../../../../store/reducers/ward';
import {getWardsIdForPatientsSearch} from '../../../../store/reducers/wardMap';
import ComputerIcon from '@mui/icons-material/Computer';
import Tooltip from '@mui/material/Tooltip';
import {PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import {getConfig} from '../../../../util/configUtil';
import {CLINICAL_CONCLUSION_INNER_STATE} from '../formsUtil';
import {useStylesBedRequest} from './bedRequestStyle';

export const DEFAULT_REQUEST_FIELD = {
    checked: null,
    who: null,
    when: null,
    user: null,
    login: null,
    timestamp: null,
    note: '',
    autoSystem: false,
};

export const DEFAULT_STATE = {
    patientId: null,
    attendanceId: null,
    visitNumber: null,
    status: 'NEW',
    followUp: false,
    currentWardId: null,
    reasonForAdmissions: '',
    activityTreatment: null,
    requested: {...DEFAULT_REQUEST_FIELD},
    requestedAcknowledge: {...DEFAULT_REQUEST_FIELD},
    allocated: {...DEFAULT_REQUEST_FIELD},
    patientReady: {
        ...DEFAULT_REQUEST_FIELD,
        checked: false,
    },
    canceled: {...DEFAULT_REQUEST_FIELD},
    canceledAcknowledge: {...DEFAULT_REQUEST_FIELD},
    sideRoom: {...DEFAULT_REQUEST_FIELD},
    ipc: {...DEFAULT_REQUEST_FIELD},
    anotherDestination: {...DEFAULT_REQUEST_FIELD},
    withinHour: {...DEFAULT_REQUEST_FIELD},
    onHoldWard: {...DEFAULT_REQUEST_FIELD},
    patientPost: {...DEFAULT_REQUEST_FIELD},
    destination: {
        hospitalId: null,
        wardId: null,
        wardName: null,
        bayId: null,
        bayName: null,
        bedId: null,
        bedName: null,
    },
    otherData: null,
};

const BedRequestFormExternal = (props) => {
    const {t} = useTranslation();
    const classes = useStylesBedRequest();
    const dispatch = useDispatch();

    // User object
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // Selectors
    const patientAddData = useSelector(additionalData);
    const isMultiHospitalMode = useSelector(multiHospitalMode);

    // Form state
    const [form, setForm] = useState(DEFAULT_STATE);
    const [saveData, setSaveData] = useState(false);
    const [formSaved, setFormSaved] = useState(false);
    const [createNewForm, setCreateNewForm] = useState(false);
    const [isBedReady, setIsBedReady] = useState('Not Ready');
    const [isCloseBROpen, openCloseBR, closeCloseBR] = useOpenState(false, true);
    const [clinician, setClinician] = useState(null);

    // Comment
    const [isCommentOpen, openComment, closeComment] = useOpenState(false, true);
    const [comment, setComment] = useState('');
    const type = useRef(null);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const dropdownsWithCliniciansAndNurses = getConfig('dropdownsWithCliniciansAndNurses', false);

    // Dict
    const [nursesCliniciansList] = useCliniciansAndNursesDict('', !dropdownsWithCliniciansAndNurses);
    // const [activityTreatment, activityTreatmentLoading] = useDict('ActivityTreatment', null, 'deactivated ne true');
    const [bedOptions, bedOptionsLoading] = useDict('MapBedOptions');
    const [specialty, specialtyLoading] = useDict('Specialties', 'division,description', 'relevent eq true');
    const [sideRoomReason, sideRoomReasonLoading] = useDict('SideRoomReasons');

    // Accordions
    const [expanded, setExpanded] = React.useState(['requested']);

    // Permission
    const isCSM = dispatch(hasPermission(Permissions.BM_CAN_MANAGE_BED_REQUEST_FROM));

    useEffect(() => {
        const newFilter = 'deactivated ne true';
        API.getDictByURL(`/$dict/ClinicalConclusionClinicians?$filter=${encodeURIComponent(newFilter)}`)
            .then((dictData) => {
                if (dictData) {
                    let findCC = null;
                    for (let clinician of dictData.__data) {
                        if (clinician.userName === userObject?.userName) {
                            findCC = clinician;
                        }
                    }
                    setClinician(findCC || userFullName);
                }
            });
    }, []);

    useEffect(() => {
        if (!_.isEmpty(props.patient?.bedRequests)) {
            const first = _.find(props.patient?.bedRequests, br => !br.followUp && br.status === 'NEW');
            setForm(first || DEFAULT_STATE);
            setFormSaved(!!first);
        }
    }, [props.patient]);

    useEffect(() => {
        if (createNewForm) {
            setCreateNewForm(false);
            handleSubmit(true);
            setSaveData(true);
        }
    }, [createNewForm]);

    useEffect(() => {
        if (!_.isEmpty(nursesCliniciansList) && form.requested && !form.requested.checked) {
            const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
            let obj = _.cloneDeep(form.requested);
            obj.checked = true;
            obj.when = moment();
            obj.timestamp = moment().toISOString();
            obj.user = userFullName;
            obj.login = userObject?.userName;
            obj.who = clinicianUser || null;
            setForm(prevState => ({
                ...prevState,
                requested: obj,
            }));
            setSaveData(true);
        }
    }, [form, nursesCliniciansList]);

    useEffect(() => {
        const checkBedReady = async () => {
            let isOccupied = false;
            let haveStatus = false;
            if (form.allocated.checked && form.destination.bedId) {
                await API.checkBedOccupied(form.destination.wardId, form.destination.bedId)
                    .then(r => isOccupied = r);
                if (!isOccupied) {
                    let wardId = form?.destination?.wardId;
                    if (isMultiHospitalMode) {
                        wardId = dispatch(getWardsIdForPatientsSearch([wardId], form?.destination?.hospitalId));
                    }

                    await API.getWardData(_.isArray(wardId) ? wardId[0] : wardId)
                        .then((res) => haveStatus = !!res?.bedStatuses?.[form.destination.bedId]);
                }

                if (isOccupied) setIsBedReady('Occupied');
                else setIsBedReady(haveStatus ? 'Not Ready' : 'Ready');
            }
        };
        checkBedReady();
    }, [form?.destination]);

    useEffect(() => {
        if (!form?.ipc?.checked && !_.isEmpty(patientAddData?.other?.alarms) && !saveData) {
            const infection = _.filter(patientAddData?.other?.alarms, alarm => _.toLower(alarm?.group) === 'infection control' && moment(alarm?.expireDate).isSameOrAfter(moment()));
            if (!_.isEmpty(infection)) {
                let cloneForm = _.cloneDeep(form);
                let clone = cloneForm.ipc || DEFAULT_REQUEST_FIELD;
                clone.checked = true;
                clone.autoSystem = true;
                addUserChange(clone);

                cloneForm.ipc = clone;
                setForm(cloneForm);
                setSaveData(true);
            }
        }
    }, [form, patientAddData, saveData]);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons(getMoreButtons());
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSubmit);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(!saveData || props.disableSave);
        }
    }, [form, saveData, props.disableSave]);

    const addUserChange = (obj) => {
        obj.user = userFullName;
        obj.login = userObject?.userName;
        obj.timestamp = moment().toISOString();
    };

    const handleAddComment = () => {
        let clone = _.cloneDeep(form);
        clone[type.current].note = comment;
        addUserChange(clone[type.current], type.current);
        setForm(clone);
        setSaveData(true);

        setComment('');
        type.current = null;
        closeComment();
    };

    const handleCloseComment = (e, reason) => {
        if (reason !== 'backdropClick') {
            setComment('');
            type.current = null;
            closeComment(e, reason);
        }
    };

    const handleChangeText = (e) => {
        const {value, name} = e.target;
        setForm(prevState => ({
            ...prevState,
            [name]: value,
        }));
        setSaveData(true);
    };

    const handleChangeDict = (name, value) => {
        setForm(prevState => ({
            ...prevState,
            [name]: value,
        }));
        setSaveData(true);
    };

    const handleChangeDictOtherData = (name, value) => {
        setForm(prevState => ({
            ...prevState,
            otherData: {
                ...prevState.otherData,
                [name]: value,
            },
        }));
        setSaveData(true);
    };

    const handleChangeTextOtherData = (event) => {
        const {name, value} = event.target;

        setForm(prevState => ({
            ...prevState,
            otherData: {
                ...prevState.otherData,
                [name]: value,
            },
        }));
        setSaveData(true);
    };

    const validateForm = () => {
        let isValid = true;
        if (isStatusInvalid(form.requested) || isStatusInvalid(form.canceled)) {
            isValid = false;
            dispatch(setSnackbar({type: 'warning', message: t('Status clinician and timestamp must be selected.')}));
        } else if (!form.specialty) {
            isValid = false;
            dispatch(setSnackbar({type: 'warning', message: t('Specialty Requested must be selected.')}));
        } else if (form?.allocated?.checked && !form?.destination?.wardId) {
            isValid = false;
            dispatch(setSnackbar({type: 'warning', message: 'Please select at least a ward.'}));
        }
        return isValid;
    };

    const isStatusInvalid = (field) => {
        if (field?.checked) _.isNil(field.when);
        return false;
    };

    const handleSubmit = (newForm) => {
        if (validateForm()) {
            let data = _.cloneDeep(form);
            if (!data.patientId) data.patientId = props?.patient?.patient?.__id;
            if (!data.attendanceId) data.attendanceId = props?.patient?.admission?.__id;
            if (!data.visitNumber) data.visitNumber = props?.patient?.admission?.attendanceId;
            if (!data.currentWardId) data.currentWardId = props?.patient?.wardId;

            // clear object
            delete data.__href;
            delete data.__type;
            delete data.__version;
            delete data._createdBy;
            delete data._createdDate;

            // Auto acknowledge if other status was selected
            if (!data?.requestedAcknowledge?.checked && (data?.allocated?.checked || data?.canceled?.checked)) {
                addUserChange(data.requestedAcknowledge);
                data.requestedAcknowledge.checked = true;
            }

            API.saveBedRequest(data)
                .then((res) => {
                    dispatch(addNoteForPatient(props.patient?.patient.__id, {
                        note: Notes.getBedRequestExternalNote(res),
                        noteType: 'bedRequest',
                        patientsInWardsRef: [props.patient.__id],
                        episodeId: props?.patient?.admission?.consultantEpisodeId || null,
                    }, props.patient.__id));
                    dispatch(setSnackbar({
                        type: 'success',
                        message: t('Bed Request saved.'),
                    }));

                    const dataToSave = [];
                    if (props.patientType !== PATIENT_CONTEXT_TYPE.INPATIENT && data.patientReady.checked && !data.canceled.checked && props.patient?.clinicalConclusion?.status !== 'Admitted') {
                        let clinicalConclusion = CLINICAL_CONCLUSION_INNER_STATE;
                        clinicalConclusion.who = clinician;
                        clinicalConclusion.when = moment().toISOString();
                        clinicalConclusion.login = userObject?.userName;
                        clinicalConclusion.status = 'Admitted';
                        clinicalConclusion.timestamp = moment().toISOString();
                        dataToSave.push({
                            name: 'clinicalConclusion',
                            value: clinicalConclusion,
                            noteToAdd: Notes.getClinicalConclusionNote(clinicalConclusion),
                        });
                    }

                    let clone = _.cloneDeep(props.patient?.bedRequests || []);
                    const index = _.findIndex(clone, {__id: res?.__id});
                    if (index !== -1) {
                        clone[index] = res;
                    } else {
                        clone.push(res);
                    }
                    dataToSave.push({
                        name: 'bedRequests',
                        value: clone,
                        onlyModify: true,
                    });

                    if (!_.isEmpty(dataToSave)) props.onChange(dataToSave);
                })
                .catch((e) => {
                    console.error(e);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: t('There was a problem with saving bed request.'),
                    }));
                });
        } else {
            return false;
        }
    };

    const renderComment = () => {
        const handleCommentChange = (e) => {
            const {value} = e.target;
            setComment(value);
        };

        return (
            <AgyleTextField
                label={t('Text')}
                debounce
                name="comment"
                value={comment}
                onChange={handleCommentChange}
                fullWidth
                noBorder={false}
                withNewBackground
            />
        );
    };

    const renderGroup = (field, label, labelSelect, clinician, commentLabel, nullStateSwitch, withinHour, noAsNull, filedDependCheck, checkRequested, alternativeLabel, checkCSM) => {
        const handleChangeSwitch = (val) => {
            let cloneForm = _.cloneDeep(form);
            let clone = cloneForm[field];
            clone.checked = val;
            clone.autoSystem = false;
            addUserChange(clone);

            if (clinician) {
                if (_.isNull(val) || (!nullStateSwitch && val === false)) {
                    clone.who = null;
                    clone.when = null;
                } else {
                    if (!clone?.who) {
                        const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
                        clone.who = clinicianUser || null;
                    }
                    if (!clone?.when) clone.when = moment().toISOString();
                }
            }

            // Add time to track
            if (withinHour) {
                if (!cloneForm.otherData) cloneForm.otherData = {};
                cloneForm.otherData.withinHourTimesToTrack = val === true ? moment().add(1, 'hour').toISOString() : null;
            }

            cloneForm[field] = clone;
            setForm(cloneForm);
            setSaveData(true);
        };

        const handleOpenComment = () => {
            type.current = field;
            setComment(form[field]?.note || '');
            openComment();
        };

        const handleChangeClinician = (_e, val) => {
            let clone = _.cloneDeep(form);
            clone[field].who = val;
            addUserChange(clone[field]);
            setForm(clone);
            setSaveData(true);
        };

        const handleChangeDate = (val) => {
            let clone = _.cloneDeep(form);
            clone[field].when = moment(val).toISOString();
            addUserChange(clone[field]);
            setForm(clone);
            setSaveData(true);
        };
        let switchDisable = checkRequested ? !form?.requested?.checked : false;
        if (filedDependCheck) switchDisable = !form[filedDependCheck || field]?.checked;
        if (field === 'requested' && formSaved) switchDisable = true;
        const isChecked = form[filedDependCheck || field]?.checked;

        return (
            <>
                <div className={classes.groupRoot}>
                    <Grid container direction="row" alignItems="end" justifyContent="space-between">
                        <FormLabel>
                            {t(label)}
                        </FormLabel>

                        <div className={classes.centerRow} style={{gap: 5}}>
                            {form[field]?.autoSystem &&
                                <Tooltip title={t('Automatically filled by the system')}>
                                    <ComputerIcon/>
                                </Tooltip>
                            }

                            <AgyleBlockSwitch
                                onChange={handleChangeSwitch}
                                value={form[field]?.checked}
                                nullState={nullStateSwitch}
                                style={{marginRight: 10}}
                                disabled={switchDisable || (checkCSM ? !isCSM : false)}
                                customLabels={(!nullStateSwitch && !noAsNull) ? {no: t(alternativeLabel ? 'No' : 'N/R')} : null}
                            />

                            {!alternativeLabel &&
                                <IconButton
                                    size="small"
                                    onClick={handleOpenComment}
                                    disabled={!isChecked || (checkCSM ? !isCSM : false)}
                                    color="primary"
                                    className={classes.iconButton}
                                >
                                    <NoteAddOutlinedIcon/>
                                </IconButton>
                            }
                        </div>
                    </Grid>

                    {!_.isNull(form[field]?.user) &&
                        <div className={classes.userLog}>
                            {`${form[field]?.user} ${moment(form[field]?.timestamp).format(defaultDateTimeFormat)}`}
                        </div>
                    }

                    {!_.isEmpty(form[field]?.note) &&
                        <AgyleTextField
                            value={form[field]?.note}
                            onChange={() => null}
                            readOnyl
                            fullWidth
                            noBorder={false}
                            withNewBackground
                        />
                    }
                </div>

                {clinician &&
                    <Grid container direction="row" className={classes.clinician} spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <AgyleDictionaryField
                                label={`${labelSelect} User`}
                                onChange={handleChangeClinician}
                                options={nursesCliniciansList}
                                groupBy={opt => dropdownsWithCliniciansAndNurses ? opt.group : ''}
                                value={form[field]?.who}
                                disabled={!isChecked}
                                error={isChecked && !form[field]?.who}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <AgyleDatePicker
                                label={`${labelSelect} Date and Time`}
                                onChange={handleChangeDate}
                                value={form[field]?.when}
                                type="dateTime"
                                disabled={!isChecked}
                                maxDate={moment()}
                                minDate={calculateArrivalDate(props.patient)}
                                error={isChecked && !form[field]?.when}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>
                }
            </>
        );
    };

    const renderAcknowledge = (field, label) => {
        const handleChangeAck = () => {
            let cloneForm = _.cloneDeep(form);
            let clone = cloneForm[field];
            clone.checked = !clone.checked;
            addUserChange(clone);

            cloneForm[field] = clone;
            setForm(cloneForm);
            setSaveData(true);
        };

        return (
            <>
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{marginTop: 10}}
                >
                    <div>
                        <FormLabel>
                            {t(label)}
                        </FormLabel>
                    </div>

                    <div style={{marginRight: -2}}>
                        <AgyleButton
                            size="medium"
                            onClick={handleChangeAck}
                            // variant={form[field]?.checked ? 'contained' : 'outlined'}
                            variant="contained"
                            color="primary"
                            width="133px"
                            disabled
                        >
                            {t(form[field]?.checked ? 'Confirmed' : 'Not confirmed')}
                        </AgyleButton>
                    </div>
                </Grid>

                {!_.isNull(form[field]?.user) &&
                    <div className={classes.userLog}>
                        {`${form[field]?.user} ${moment(form[field]?.timestamp).format(defaultDateTimeFormat)}`}
                    </div>
                }

                {!_.isEmpty(form[field]?.note) &&
                    <div className={classes.comment}>
                        {form[field]?.note}
                    </div>
                }
            </>
        );
    };

    const handleAccordionChange = (panel) => {
        if (expanded.includes(panel)) {
            setExpanded(_.filter(expanded, e => e !== panel));
        } else {
            setExpanded([...expanded, panel]);
        }
    };

    const handleNewForm = () => {
        closeCloseBR();
        setForm(prevState => ({
            ...prevState,
            status: 'CANCELED',
        }));
        setCreateNewForm(true);
    };

    const getMoreButtons = () => {
        if (form?.canceled?.checked) {
            return ([
                <AgyleButton
                    colour="primary"
                    onClick={openCloseBR}
                >
                    {t('Close current bed request')}
                </AgyleButton>,
            ]);
        }
    };

    const renderSideRoomReason = () => {
        return (
            <>
                <div className={classes.row}>
                    <AgyleDictionaryField
                        label={t('Side Room Reason')}
                        onChange={(_e, val) => {
                            handleChangeDictOtherData('sideRoomReason', val);
                            handleChangeTextOtherData({target: {name: 'sideRoomReasonText', value: null}});
                        }}
                        options={sideRoomReason}
                        value={form.otherData?.sideRoomReason}
                        loading={sideRoomReasonLoading}
                        disabled={!form?.requested?.checked}
                    />
                </div>

                {form.otherData?.sideRoomReason?.showText &&
                    <div className={classes.row}>
                        <AgyleTextField
                            label={t('Note')}
                            name="sideRoomReasonText"
                            onChange={handleChangeTextOtherData}
                            value={form.otherData?.sideRoomReasonText}
                            fullWidth
                            style={{marginTop: 10}}
                            debounce
                            disabled={!form?.requested?.checked}
                        />
                    </div>
                }
            </>
        );
    };

    const renderReadyIndicators = () => {
        const isPatientReady = form?.patientReady?.checked;

        const setPtAsReady = () => {
            let cloneForm = _.cloneDeep(form);
            let clone = cloneForm.patientReady || DEFAULT_REQUEST_FIELD;
            clone.checked = !clone.checked;
            addUserChange(clone);

            if (_.isNull(clone.checked)) {
                clone.who = null;
                clone.when = null;
            } else {
                if (!clone?.who) {
                    const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
                    clone.who = clinicianUser || null;
                }
                if (!clone?.when) clone.when = moment().toISOString();
            }

            cloneForm.patientReady = clone;
            setForm(cloneForm);
            setSaveData(true);
        };

        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                {form?.destination?.bedId &&
                    <div className={clsx(classes.divRow, 'separate')}>
                        <FormLabel style={{width: 100}}>
                            {t('Bed Ready')}
                        </FormLabel>

                        <AgyleButton
                            variant="outlined"
                            startIcon={isBedReady ? <CheckCircleOutlinedIcon/> : <CancelOutlinedIcon/>}
                            className={clsx(classes.statusBtn, 'bed', _.camelCase(isBedReady))}
                            disableTouchRipple
                            size="small"
                        >
                            {isBedReady}
                        </AgyleButton>
                    </div>
                }

                <div className={clsx(classes.divRow, 'separate')}>
                    <FormLabel style={{width: 100}}>
                        {t('Patient Ready')}
                    </FormLabel>

                    <AgyleButton
                        variant="outlined"
                        onClick={setPtAsReady}
                        startIcon={isPatientReady ? <CheckCircleOutlinedIcon/> : null}
                        className={clsx(classes.statusBtn, 'patient', isPatientReady && 'ready')}
                        size="small"
                    >
                        {isPatientReady ? 'Patient is Ready' : 'Set the Patient as Ready'}
                    </AgyleButton>
                </div>
            </div>
        );
    };

    return (
        <>
            <AgyleAccordion
                expanded={expanded.includes('requested')}
                label={t('Requested')}
                onChange={() => handleAccordionChange('requested')}
                formBackground
            >
                {renderGroup('requested', 'Requested', 'Requesting', true)}

                <Divider className={classes.divider}/>

                <div className={classes.row}>
                    <AgyleDictionaryField
                        label={t('Specialty Requested')}
                        onChange={(_e, val) => handleChangeDict('specialty', val)}
                        options={specialty}
                        value={form.specialty}
                        loading={specialtyLoading}
                        disabled={!form?.requested?.checked}
                        error={!form.specialty}
                        groupBy={opt => opt.division}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>

                <div className={classes.row}>
                    <AgyleTextField
                        debounce
                        label={t('Reason for Admissions')}
                        name="reasonForAdmissions"
                        value={form.reasonForAdmissions || ''}
                        onChange={handleChangeText}
                        fullWidth
                        disabled={!form?.requested?.checked}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>

                <Divider className={classes.divider}/>

                {/*{renderGroup('onHoldWard', 'On Hold Ward', 'On Hold Ward', false, 'Comment', false, false, true, 'requested')}*/}
                {renderGroup('sideRoom', 'Side Room', 'Side Room', false, 'Reason', false, false, false, 'requested')}
                {form?.sideRoom?.checked && renderSideRoomReason()}
                {/*{renderGroup('patientPost', 'Patient Post Take Complete', 'Patient Post Take Complete', false, 'Comment', false, false, true, 'requested')}*/}
                {renderGroup('ipc', 'IPC requirements', 'IPC requirements', false, 'Reason', false, false, false, 'requested')}

                <Divider className={classes.divider}/>

                <AgyleDictionaryField
                    label={t('Bed Requirements Equipment')}
                    options={bedOptions}
                    loading={bedOptionsLoading}
                    isMultiple
                    onChange={(_e, val) => handleChangeDictOtherData('bedOptions', val)}
                    value={form.otherData?.bedOptions || []}
                    getOptionLabel={option => option.__description}
                    disabled={!form?.requested?.checked}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />

                <AgyleTextField
                    label={t('Special Bed Requirements')}
                    name="requirements"
                    onChange={handleChangeTextOtherData}
                    value={form.otherData?.requirements}
                    fullWidth
                    style={{marginTop: 10}}
                    debounce
                    disabled={!form?.requested?.checked}
                    size="large"
                    noBorder={false}
                    withNewBackground
                />

                {form?.requested?.checked &&
                    renderAcknowledge('requestedAcknowledge', 'Acknowledge Request')
                }
            </AgyleAccordion>

            <AgyleAccordion
                expanded={expanded.includes('allocated')}
                label={t('Allocated')}
                onChange={() => handleAccordionChange('allocated')}
                formBackground
            >
                {renderGroup('allocated', 'Allocated', 'Allocated', false, 'WARD Feedback', false, false, false, null, true, false, true)}
                <BedRequestWardSelect
                    form={form}
                    setForm={setForm}
                    setSaveData={setSaveData}
                    disabled={!isCSM}
                    withNewBackground
                />
                {/*{renderGroup('withinHour', 'Within an hour', 'Within an hour', false, 'Comment', true, true, false, 'allocated')}*/}
            </AgyleAccordion>

            <AgyleAccordion
                expanded={expanded.includes('ready')}
                label={t('Ready')}
                onChange={() => handleAccordionChange('ready')}
                formBackground
            >
                {renderReadyIndicators()}
            </AgyleAccordion>

            <AgyleAccordion
                expanded={expanded.includes('canceled')}
                label={t('Cancelled')}
                onChange={() => handleAccordionChange('canceled')}
                formBackground
            >
                {renderGroup('canceled', 'Cancelled', 'Cancelling', 'Cancelled', true, false, false, false, false, null)}
                {form?.canceled?.checked &&
                    renderAcknowledge('canceledAcknowledge', 'Acknowledge Cancel')
                }
            </AgyleAccordion>

            <MessageBox
                open={isCommentOpen}
                title={t('Add note')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleAddComment}
                onClose={handleCloseComment}
                messageObject={renderComment()}
                fullWidth
            />

            <MessageBox
                open={isCloseBROpen}
                title={t('Close current Bed Request')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleNewForm}
                onClose={closeCloseBR}
                fullWidth
                message={t('Are you sure you want to completely close this bed request? ' +
                    'This will cause it to disappear from the list in bed management system and allow you to create a new one.')}
            />
        </>
    );
};

export default BedRequestFormExternal;